import type { Daycare, Payment, Price, Service, Stay } from '../../../../packages/contracts/src/index.js';
export type BillingUnit = Price['billingUnit'];
export type PayableSourceType = 'daycare' | 'stay';

export interface PriceLookups {
  readonly byCategory: Partial<Record<Service['category'], Price>>;
  readonly byBillingUnit: Partial<Record<BillingUnit, Price>>;
  readonly byCategoryAndBillingUnit: Partial<Record<Service['category'], Partial<Record<BillingUnit, Price>>>>;
}

export function buildPriceLookups(services: Service[], pricesByServiceId: Record<string, Price>): PriceLookups {
  const entries = services
    .map((service) => ({ service, price: pricesByServiceId[service.id] }))
    .filter((entry): entry is { service: Service; price: Price } => Boolean(entry.price))
    .sort((first, second) => {
      const byDate = second.price.effectiveFrom.localeCompare(first.price.effectiveFrom);
      if (byDate !== 0) return byDate;
      return second.price.createdAt.localeCompare(first.price.createdAt);
    });

  const byBillingUnit = Object.values(pricesByServiceId)
    .sort((first, second) => {
      const byDate = second.effectiveFrom.localeCompare(first.effectiveFrom);
      if (byDate !== 0) return byDate;
      return second.createdAt.localeCompare(first.createdAt);
    })
    .reduce<Partial<Record<BillingUnit, Price>>>((byUnit, price) => {
      byUnit[price.billingUnit] ??= price;
      return byUnit;
    }, {});

  return {
    byBillingUnit,
    byCategory: entries.reduce<Partial<Record<Service['category'], Price>>>((byCategory, entry) => {
      byCategory[entry.service.category] ??= entry.price;
      return byCategory;
    }, {}),
    byCategoryAndBillingUnit: entries.reduce<Partial<Record<Service['category'], Partial<Record<BillingUnit, Price>>>>>((byCategory, entry) => {
      byCategory[entry.service.category] ??= {};
      byCategory[entry.service.category]![entry.price.billingUnit] ??= entry.price;
      return byCategory;
    }, {}),
  };
}

export function latestUsablePrice(prices: Price[], currentDate: string) {
  const activePrices = prices.filter((price) => price.active !== false);
  const currentPrices = activePrices.filter((price) => price.effectiveFrom <= currentDate && (!price.effectiveTo || price.effectiveTo >= currentDate));
  const historicalPrices = activePrices.filter((price) => price.effectiveFrom <= currentDate);
  return sortLatestPrices(currentPrices)[0] ?? sortLatestPrices(historicalPrices)[0] ?? sortLatestPrices(activePrices)[0];
}

export function dueCentsForStay(stay: Stay, services: Service[], prices: Record<string, Price>, lookups: PriceLookups) {
  void lookups;
  const nights = nightsBetween(stay.checkInDate, stay.checkOutDate);
  const sourcePrices = servicePricesForSource(stay, prices);
  return serviceIdsDueCents(serviceIdsForSource(stay), services, sourcePrices, { hours: 1, nights });
}

export function dueCentsWithFrozenBill(payments: Payment[], sourceType: PayableSourceType, sourceId: string, calculatedDueCents: number) {
  return Math.max(frozenBillTotalCentsForSource(payments, sourceType, sourceId) ?? 0, calculatedDueCents);
}

export function dueCentsForDaycare(daycare: Daycare, services: Service[], prices: Record<string, Price>, lookups: PriceLookups) {
  void lookups;
  const hours = hoursBetween(daycare.startAt, daycare.endAt);
  return serviceIdsDueCents(serviceIdsForSource(daycare), services, servicePricesForSource(daycare, prices), { hours, nights: 1 });
}

export function paidCentsForSource(payments: Payment[], sourceType: PayableSourceType, sourceId: string) {
  return payments.reduce((total, payment) => {
    const itemTotal = payment.items
      .filter((item) =>
        (item.sourceType === sourceType && item.sourceId === sourceId) ||
        (sourceType === 'stay' && item.relatedStayId === sourceId) ||
        (sourceType === 'daycare' && item.relatedDaycareId === sourceId),
      )
      .reduce((itemSum, item) => itemSum + item.amountCents * item.quantity, 0);
    if (itemTotal > 0) return total + itemTotal;
    return payment.sourceType === sourceType && payment.sourceId === sourceId ? total + payment.totalCents : total;
  }, 0);
}

export function frozenBillTotalCentsForSource(payments: Payment[], sourceType: PayableSourceType, sourceId: string) {
  return payments.reduce<number | undefined>((current, payment) => {
    const matchingItem = payment.items.find((item) => item.sourceType === sourceType && item.sourceId === sourceId && typeof item.billTotalCents === 'number');
    const nextTotal =
      matchingItem?.billTotalCents ??
      (payment.sourceType === sourceType && payment.sourceId === sourceId ? payment.billTotalCents : undefined);
    if (typeof nextTotal !== 'number') return current;
    return typeof current === 'number' ? Math.max(current, nextTotal) : nextTotal;
  }, undefined);
}

export function billReferenceForSource(payments: Payment[], sourceType: PayableSourceType, sourceId: string, fallback: string) {
  const payment = payments.find((candidate) => candidate.sourceType === sourceType && candidate.sourceId === sourceId && candidate.billReference);
  const itemPayment = payments.find((candidate) => candidate.items.some((item) => item.sourceType === sourceType && item.sourceId === sourceId && item.billReference));
  return payment?.billReference ?? itemPayment?.items.find((item) => item.sourceType === sourceType && item.sourceId === sourceId)?.billReference ?? fallback;
}

export function paymentStatus(dueCents: number, paidCents: number) {
  if (dueCents > 0 && paidCents >= dueCents) return { label: 'Pagado', tone: 'paid' as const };
  if (paidCents > 0) return { label: 'Abono', tone: 'partial' as const };
  return { label: 'Pendiente', tone: 'pending' as const };
}

export function nightsBetween(checkInDate: string, checkOutDate: string) {
  const start = new Date(`${checkInDate}T00:00:00`).getTime();
  const end = new Date(`${checkOutDate}T00:00:00`).getTime();
  return Math.max(1, Math.ceil((end - start) / 86_400_000));
}

export function hoursBetween(startAt: string, endAt?: string) {
  const start = new Date(startAt).getTime();
  const end = endAt ? new Date(endAt).getTime() : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 1;
  return Math.max(1, Math.ceil((end - start) / 3_600_000));
}

type PriceLike = Pick<Price, 'amountCents' | 'billingUnit'>;

function serviceIdsDueCents(serviceIds: string[], services: Service[], prices: Record<string, PriceLike>, duration: { hours: number; nights: number }) {
  return serviceIds.reduce((total, serviceId) => {
    const service = services.find((item) => item.id === serviceId);
    const price = prices[serviceId];
    if (!service || !price) return total;
    return total + price.amountCents * quantityForUnit(price.billingUnit, duration.hours, duration.nights);
  }, 0);
}

export function serviceIdsForSource(source: { serviceIds?: readonly string[]; dogServiceIds?: Record<string, readonly string[]> }) {
  const dogServiceIds = Object.values(source.dogServiceIds ?? {}).flat();
  const ids = dogServiceIds.length > 0 ? dogServiceIds : (source.serviceIds ?? []);
  return ids.filter(Boolean);
}

export function uniqueServiceIdsForSource(source: { serviceIds?: readonly string[]; dogServiceIds?: Record<string, readonly string[]> }) {
  return [...new Set(serviceIdsForSource(source))];
}

export function servicePricesForSource(source: { servicePriceLocks?: Record<string, PriceLike> }, prices: Record<string, Price>) {
  return { ...prices, ...(source.servicePriceLocks ?? {}) };
}

export function dogIdsForSource(source: { dogId: string; dogIds?: readonly string[] }) {
  return source.dogIds?.length ? source.dogIds : [source.dogId];
}

export function serviceIdsByDogForSource(source: { dogId: string; dogIds?: readonly string[]; serviceIds?: readonly string[]; dogServiceIds?: Record<string, readonly string[]> }) {
  return dogIdsForSource(source).map((dogId) => [
    dogId,
    source.dogServiceIds?.[dogId] ?? (source.dogServiceIds ? [] : (source.serviceIds ?? [])),
  ] as const);
}

export function extraStayDays(stay: Pick<Stay, 'checkOutDate'>) {
  const plannedEnd = new Date(`${stay.checkOutDate}T23:59:59`).getTime();
  const diff = Date.now() - plannedEnd;
  return diff > 0 ? Math.ceil(diff / 86_400_000) : 0;
}

export function extraDaycareHours(daycare: Pick<Daycare, 'endAt'>) {
  if (!daycare.endAt) return 0;
  const diff = Date.now() - new Date(daycare.endAt).getTime();
  return diff > 0 ? Math.ceil(diff / 3_600_000) : 0;
}

export function quantityForUnit(unit: BillingUnit, hours: number, nights: number) {
  if (unit === 'hour') return hours;
  if (unit === 'night') return nights;
  if (unit === 'entrance') return 1;
  return 1;
}

function sortLatestPrices(prices: Price[]) {
  return [...prices].sort((first, second) => {
    const byDate = second.effectiveFrom.localeCompare(first.effectiveFrom);
    if (byDate !== 0) return byDate;
    return second.createdAt.localeCompare(first.createdAt);
  });
}
